import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'moment';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  AlertSingleLine,
  DataTable,
  Loader,
  SdirButton,
  SdirDatePicker,
  SdirModal,
  SdiTrash,
  SearchBar,
  StatusLabel,
  TextLink,
} from '@sdir/sds';
import { formatDateString } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import {
  SharedCompetenceRequestResponse,
  V1SharedcompetenceCreatesharedcompetencerequestRequestPostRequest,
  V1SharedcompetenceApprovesharedcompetencerequestRequestPostRequest,
} from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import { ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { organizationApi, personalCertificateSharedCompetenceApi } from '../../../httpclient';
import { Card } from '../../Atoms';
import {
  SharedCompetenceTableHeader,
  ShareRequestCompetenceTableHeader,
} from '../../../helpers/TableHeaders';

const toStatusVariant = (status?: 'Approved' | 'Pending' | 'Rejected') => {
  switch (status) {
    case 'Approved':
      return 'success';
    case 'Pending':
      return 'secondary';
    case 'Rejected':
      return 'error';
    default:
      return 'primary';
  }
};

type ShareCompetenceProps = {
  personalNr?: string;
};

export const ShareCompetence: React.FC<ShareCompetenceProps> = ({ personalNr }) => {
  const intl = useIntl();
  const [selectedOrganization, setSelectedOrganization] = useState<any>();
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [showExpiryModal, setShowExpiryModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<SharedCompetenceRequestResponse>();

  const { result: searchResults, callApi: onSearch } = useApi({
    apiFunction: (q) => organizationApi.v1OrganizationSearchfromrouteSearchGet(q),
    callApiOnInit: false,
  });

  const {
    loading: submitting,
    errorObject: submitError,
    callApi: onShare,
  } = useApi({
    apiFunction: (q) =>
      // eslint-disable-next-line max-len
      personalCertificateSharedCompetenceApi.v1SharedcompetenceCreatesharedcompetencerequestRequestPost(
        q
      ),
    callApiOnInit: false,
    successCallback: () => {
      onCloseExpiryModal();
      getShareRequests();
    },
  });

  const {
    loading: loadingShared,
    result: shareResults,
    errorObject: getSharedError,
    callApi: getShared,
  } = useApi({
    apiFunction: () =>
      personalCertificateSharedCompetenceApi.v1SharedcompetenceGetsharedcompetenceGet(),
    callApiOnInit: true,
  });

  const {
    loading: loadingShareRequests,
    result: shareRequestsResults,
    errorObject: getShareRequestsError,
    callApi: getShareRequests,
  } = useApi({
    apiFunction: () =>
      personalCertificateSharedCompetenceApi.v1SharedcompetenceGetsharedcompetencerequestsGet(),
    callApiOnInit: true,
  });

  const {
    loading: loadingDeleteShared,
    errorObject: deleteSharedError,
    callApi: onDeleteShared,
  } = useApi({
    apiFunction: (k) =>
      // eslint-disable-next-line max-len
      personalCertificateSharedCompetenceApi.v1SharedcompetenceDeletesharedcompetenceSharingKeyDelete(
        k
      ),
    callApiOnInit: false,
    successCallback: () => {
      getShared();
    },
  });

  const {
    loading: loadingDeleteRequest,
    errorObject: deleteRequestError,
    callApi: onDeleteRequest,
  } = useApi({
    apiFunction: (k) =>
      // eslint-disable-next-line max-len
      personalCertificateSharedCompetenceApi.v1SharedcompetenceDeletesharedcompetencerequestSharingKeyDelete(
        k
      ),
    callApiOnInit: false,
    successCallback: () => {
      getShareRequests();
    },
  });

  const {
    loading: loadingApprove,
    errorObject: approveError,
    callApi: onApprove,
  } = useApi({
    apiFunction: (p) =>
      // eslint-disable-next-line max-len
      personalCertificateSharedCompetenceApi.v1SharedcompetenceApprovesharedcompetencerequestRequestPost(
        p.key,
        p.body
      ),
    callApiOnInit: false,
    successCallback: () => {
      onCloseExpiryModal();
      getShared();
      getShareRequests();
    },
  });

  const {
    loading: loadingReject,
    errorObject: rejectError,
    callApi: onReject,
  } = useApi({
    apiFunction: (k) =>
      // eslint-disable-next-line max-len
      personalCertificateSharedCompetenceApi.v1SharedcompetenceRejectsharedcompetencerequestSharingKeyPost(
        k
      ),
    callApiOnInit: false,
    successCallback: () => {
      getShareRequests();
    },
  });

  const orgResults = useMemo(() => {
    const items = searchResults?.source?.map?.((r) => {
      return [r.name || null, r.organizationNumber || null];
    });
    return items;
  }, [searchResults?.source]);

  const tableColumnsShared = useMemo(() => {
    return SharedCompetenceTableHeader(intl?.formatMessage);
  }, [intl?.formatMessage]);

  const tableColumnsRequests = useMemo(() => {
    return ShareRequestCompetenceTableHeader(intl?.formatMessage);
  }, [intl?.formatMessage]);

  const tableDataShared = useMemo(() => {
    return (shareResults || [])?.map((item) => {
      return {
        organization: item?.organizationName || '',
        date: formatDateString(item?.createdAt || ''),
        actions: (
          <TextLink
            text={intl.formatMessage({ id: 'general.form.delete' })}
            icon={<SdiTrash size="s" height="1em" />}
            onClick={() => onDeleteShared(item?.sharedCompetenceKey)}
            type="button"
          />
        ),
      };
    });
  }, [shareResults]);

  const tableDataRequests = useMemo(() => {
    return (shareRequestsResults || [])?.map((item) => {
      return {
        organization: item?.organizationName || '',
        date: formatDateString(item?.expiresAt || ''),
        status: item?.status ? (
          <StatusLabel variant={toStatusVariant(item?.status as any)}>
            {intl.formatMessage({ id: `profile.shared.table.status.${item.status}` })}
          </StatusLabel>
        ) : (
          ''
        ),
        actions: (
          <Actions>
            {item?.status === 'Pending' && item?.initiatedBy === 'Organization' && (
              <SdirButton
                text={intl.formatMessage({ id: 'general.button.approve' })}
                onClick={() => onOpenExpiryModal(item)}
                size="sm"
                disabled={loadingApprove || loadingReject}
              />
            )}
            {item?.status === 'Pending' && item?.initiatedBy === 'Organization' && (
              <SdirButton
                text={intl.formatMessage({ id: 'general.button.reject' })}
                onClick={() => onReject(item?.sharedCompetenceKey)}
                size="sm"
                disabled={loadingApprove || loadingReject}
              />
            )}
            {item?.status === 'Pending' && (
              <TextLink
                text={intl.formatMessage({ id: 'general.form.delete' })}
                icon={<SdiTrash size="s" height="1em" />}
                onClick={() => onDeleteRequest(item?.sharedCompetenceKey)}
                type="button"
              />
            )}
          </Actions>
        ),
      };
    });
  }, [shareRequestsResults, loadingApprove, loadingReject]);

  const onOpenExpiryModal = (selected?: SharedCompetenceRequestResponse) => {
    setShowExpiryModal(true);
    setSelectedItem(selected);
  };

  const onCloseExpiryModal = () => {
    setShowExpiryModal(false);
    setSelectedItem(undefined);
  };

  const onSearchOrganization = (query: string) => {
    return onSearch(query);
  };

  const onSelectOrganization = useCallback((value: any) => {
    setSelectedOrganization(value);
  }, []);

  const onConfirmShare = () => {
    if (selectedItem) {
      const body: V1SharedcompetenceApprovesharedcompetencerequestRequestPostRequest = {
        expiresAt: expiryDate ? moment(expiryDate).format('YYYY-MM-DD') : null,
      };
      onApprove({ key: selectedItem?.sharedCompetenceKey, body });
    } else if (selectedOrganization) {
      onShareWithOrganization();
    }
  };

  const onShareWithOrganization = useCallback(async () => {
    const guid = crypto?.randomUUID();
    const payload: V1SharedcompetenceCreatesharedcompetencerequestRequestPostRequest = {
      expiresAt: expiryDate ? moment(expiryDate).format('YYYY-MM-DD') : null,
      organizationNumber: selectedOrganization?.[1],
      personalIdentityNumber: personalNr,
      sharedCompetenceKey: guid,
    };
    onShare(payload);
  }, [expiryDate, personalNr, selectedOrganization]);

  return (
    <>
      <Card heading={intl.formatMessage({ id: 'profile.share.title' })}>
        <SearchWrapper>
          <SearchBar
            searchCallback={onSearchOrganization}
            placeholder={intl.formatMessage({ id: 'profile.share.search.placeholder' })}
            searchResults={orgResults}
            onSearchResultClick={onSelectOrganization}
          />
          <SdirButton
            text={intl.formatMessage({ id: 'profile.share.button' })}
            onClick={() => onOpenExpiryModal()}
            disabled={
              !selectedOrganization ||
              loadingDeleteRequest ||
              loadingShareRequests ||
              loadingDeleteShared
            }
          />
        </SearchWrapper>
      </Card>
      <Card heading={intl.formatMessage({ id: 'profile.shared.title' })}>
        {loadingShared && <Loader />}
        {!loadingShared && !!tableDataShared?.length && (
          <StyledTable columns={tableColumnsShared} data={tableDataShared} />
        )}
        {!tableDataShared?.length && (
          <AlertSingleLine
            title={intl.formatMessage({ id: 'profile.shared.noresults.title' })}
            text={intl.formatMessage({ id: 'profile.shared.noresults.text' })}
            type="info"
            hideClose
          />
        )}
      </Card>
      <Card heading={intl.formatMessage({ id: 'profile.shared.requests.title' })}>
        {loadingShareRequests && <Loader />}
        {!loadingShareRequests && !!tableDataRequests?.length && (
          <StyledTable columns={tableColumnsRequests} data={tableDataRequests} />
        )}
        {!tableDataRequests?.length && (
          <AlertSingleLine
            title={intl.formatMessage({ id: 'profile.sharerequests.noresults.title' })}
            text={intl.formatMessage({ id: 'profile.sharerequests.noresults.text' })}
            type="info"
            hideClose
          />
        )}
      </Card>
      {getSharedError && (
        <Card>
          <ErrorWithTrace
            error={getSharedError}
            trace={getSharedError?.response?.headers?.traceresponse}
          />
        </Card>
      )}
      {getShareRequestsError && (
        <Card>
          <ErrorWithTrace
            error={getShareRequestsError}
            trace={getShareRequestsError?.response?.headers?.traceresponse}
          />
        </Card>
      )}
      {deleteRequestError && (
        <Card>
          <ErrorWithTrace
            error={deleteRequestError}
            trace={deleteRequestError?.response?.headers?.traceresponse}
          />
        </Card>
      )}
      {rejectError && (
        <Card>
          <ErrorWithTrace
            error={rejectError}
            trace={rejectError?.response?.headers?.traceresponse}
          />
        </Card>
      )}
      {deleteSharedError && (
        <Card>
          <ErrorWithTrace
            error={deleteSharedError}
            trace={deleteSharedError?.response?.headers?.traceresponse}
          />
        </Card>
      )}
      <SdirModal
        visible={showExpiryModal}
        onOK={onConfirmShare}
        onCancel={onCloseExpiryModal}
        okText={intl.formatMessage({ id: 'profile.share.button' })}
        cancelText={intl.formatMessage({ id: 'general.form.cancel' })}
        disableOK={submitting || loadingApprove}
      >
        <h2>{intl.formatMessage({ id: 'profile.share.header' })}</h2>
        <p>{intl.formatMessage({ id: 'profile.share.description' })}</p>
        <SdirDatePicker
          label={intl.formatMessage({ id: 'profile.shared.table.expires' })}
          className="expiry-date-input"
          value={expiryDate}
          minDate={new Date()}
          showMonthDropdown
          showYearDropdown
          locale={intl.locale || 'no'}
          onChange={(d: Date | null) => setExpiryDate(d)}
        />
        {submitError && (
          <ErrorWrapper>
            <ErrorWithTrace
              error={submitError}
              trace={submitError?.response?.headers?.traceresponse}
            />
          </ErrorWrapper>
        )}
        {approveError && (
          <ErrorWrapper>
            <ErrorWithTrace
              error={approveError}
              trace={approveError?.response?.headers?.traceresponse}
            />
          </ErrorWrapper>
        )}
      </SdirModal>
    </>
  );
};

const SearchWrapper = styled.div`
  display: flex;

  & > *:not(:first-child) {
    margin-left: 15px;
  }
`;

const StyledTable = styled(DataTable)`
  padding: 0;
  thead tr th {
    padding-left: 0;
  }

  span[class^='StatusLabel__StyledSpan'] {
    min-width: 90px;
  }
`;

const ErrorWrapper = styled.div`
  max-width: 500px;
  margin: 30px 0;
`;

const Actions = styled.div`
  display: flex;
  & > * {
    margin-left: 5px;
  }
`;
